import axios from "axios";
import { handleOpenSnackBar } from "../utils/helper";
import {
  getAccessToken,
  setAccessToken,
  clearAccessToken,
  waitForAuthReady,
} from "./tokenStore";

export let baseUrl = process.env.REACT_APP_API_URL;

const axiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let pendingQueue = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
};

const forceLogout = (message) => {
  clearAccessToken();
  localStorage.removeItem("user_data");
  handleOpenSnackBar(
    "error",
    message || "Session expired. Please login again."
  );
  setTimeout(() => {
    window.location.href = "/login";
  }, 1500);
};

// Shared refresh — Context + interceptor dono yahi use karte hain
export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = axios
    .post(`${baseUrl}auth/refresh-token`, {}, { withCredentials: true })
    .then((res) => {
      const newToken = res.data?.data?.token;
      if (!newToken) throw new Error("No access token in refresh response");
      setAccessToken(newToken);
      return newToken;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

axiosInstance.interceptors.request.use(async (config) => {
  const url = config.url || "";
  const skipWait =
    url.includes("auth/refresh-token") || url.includes("user/login");

  if (!skipWait) {
    await waitForAuthReady();
  }

  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const code = error.response?.data?.code;

    const canRefresh =
      status === 401 &&
      !originalRequest._retry &&
      (code === "TOKEN_EXPIRED" ||
        code === "NO_TOKEN" ||
        error.response?.data?.message === "Access token expired" ||
        error.response?.data?.message === "You are not logged in!");

    if (!canRefresh) {
      if (
        status === 401 &&
        !originalRequest?.url?.includes("auth/refresh-token")
      ) {
        if (
          code === "REFRESH_EXPIRED" ||
          code === "REFRESH_REVOKED" ||
          code === "NO_REFRESH_TOKEN" ||
          code === "INVALID_TOKEN"
        ) {
          forceLogout("Session expired. Please login again.");
        }
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const newToken = await refreshAccessToken();
      processQueue(null, newToken);
      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return axiosInstance(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      forceLogout("Session expired. Please login again.");
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

const api = async (path, params, method) => {
  const options = {
    method,
    url: path,
    ...(params && { data: params }),
  };

  try {
    const response = await axiosInstance(options);
    return response;
  } catch (error) {
    if (!error.response) {
      throw new Error("Network error. Please try again.");
    }

    if (error.response.status !== 401) {
      return error.response;
    }

    throw new Error(
      error.response?.data?.message || "Session expired. Please login again."
    );
  }
};

export default api;
